<template>
  <div>
      <div class="title"> Rotating 3D Solids </div>
      <div class="date"> June 3rd, 2018 </div>

      <div class="content">
        
        <div style="float: right; margin-bottom: 1em;">
            <canvas style="cursor: pointer;" width="500px" height="400px" id="blog1"></canvas>
            <div style="text-align: center; margin: 0 0 0 0" id="blog1shapeName">Isosahedron</div>
            <div style="display: flex; justify-content: center" id="blog1controls">
                <div class="triangle-left"></div>
                <div class="circle" style="margin: 2px 1em 0 1em"></div>
                <div class="triangle-right"></div>
            </div>
        </div>
        &emsp;&emsp;This took me way too long to make. I came across a cool gif of a isosahedron and wanted to
         see if I could recreate it. I added more features: there are more shapes to look at using the controls at 
         the bottom and the user is also able to drag around the shape and rotate it themselves.
         
         <br>
         &emsp;&emsp;To create this, I needed the 
         <a class="visibleLink" href="https://en.wikipedia.org/wiki/Platonic_solid#Cartesian_coordinates">coordinates</a> 
         of the 5 platonic solids first. In creating this rotatable interface, the hard part was not rotating the coordinates, 
         as I thought it would be. Instead, the hardest part was creating a method which fills each face of the shape in the correct 
         order. Looking back, it is really simple; the faces which are furthest from the viewer (have the highest z-coordinates) 
         are drawn first. This way the nearest faces are drawn on top of the faces behind it. In this 3D coordinate system, 
         positive z corresponds to the direction facing into the screen. The source code can be found by inspecting the canvas
          element, it is by no means perfect or best implementation.
          
        <br
        >&emsp;&emsp;I feel so accomplished creating this interface, but I know nobody will probably be reading this.
        It was quite interesting to read about and finally implement. I never expected the golden ratio, 
        <a class="visibleLink" href="https://en.wikipedia.org/wiki/Golden_ratio">φ</a>, to appear in these shapes. I guess it may
        be one of the reasons an isosahedron looks so aesthetically pleasing to my eyes. Moreover, it is also weirdly interesting
        how these shapes appear naturally in chemistry simply because it is an efficient mathematical way to arrange points
        (<a class="visibleLink" href="https://en.wikipedia.org/wiki/Dodecahedrane">Dodecahedrane, C<sub>20</sub>H<sub>20</sub></a>).

      </div>
  </div>
</template>

<script>

export default {
  name: 'RotatingSolids',
  mounted: () => {

// Rotates point(x, y) given angle
function rotateCoord(ax, ay, x, y, angle) {
    x -= ax; y -= ay;
    let r = Math.sqrt((x * x) + (y * y));
    let fin = angle + Math.atan2(y, x);
    return {x: ax + r * Math.cos(fin), y: ay + r * Math.sin(fin)}
}

// Create 3D vector
function v(x, y, z) {
    return {x: x, y: y, z: z};
}

// Encapsulates Platonic Solid
class Solid {
    // nOfLinks = q of its Schläfli symbol
    constructor(points, nOfLinks, faces) {
        this.points = points;
        this.edges = [];
        this.faces = faces;
        let distance = (p1, p2) => Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2) + Math.pow(p1.z - p2.z, 2));
        let hasEdge = (array, e) => {
            if (array.includes(e))
                return true;
            for (let a of array) {
                if (a[0] == e[0] && a[1] == e[1])
                    return true;
            } 
            return false;
        };
        for (let point of points) {
            let otherPoints = points.filter(p => p !== point);

            for (let x = 0; x < nOfLinks; x++) {
                let closestPoint = otherPoints.reduce((a, b) => (distance(a, point) < distance(b, point)) ? a : b);
                let edge = [points.indexOf(point), points.indexOf(closestPoint)];
                if (!this.edges.includes(edge) && !hasEdge(this.edges, [edge[1], edge[0]])) 
                    this.edges.push(edge);
                otherPoints = otherPoints.filter(p => p !== closestPoint);
            }
        }
    }

    drawSolid(cx, x, y, scale) {
        cx.translate(x, y);
        cx.fillStyle = "blue";
        let points = this.points.map(n => {return {x:scale*n.x,y:scale*n.y, z:scale*n.z}});
        for (let p of points) {
            cx.beginPath();
            cx.arc(p.x, p.y, 1, 0, 7);
            cx.fillText(points.indexOf(p), p.x, p.y - 13);
            cx.stroke();
        }
        cx.resetTransform();
    }

    drawSolidEdges(cx, x, y, scale, edges = this.edges) {
        cx.translate(x, y);
        let points = this.points.map(n => {return {x:scale*n.x,y:scale*n.y, z:scale*n.z}});
        for (let e of edges) {
        cx.beginPath();
        cx.moveTo(points[e[0]].x, points[e[0]].y);
        cx.lineTo(points[e[1]].x, points[e[1]].y);
        cx.stroke();
        }
        cx.resetTransform();
    }

    drawFaces(cx, x, y, scale) {
        let compareZ = (a, b) => {
        let minA = a.reduce((c, n) => c + this.points[n].z, 0);
        let minB = b.reduce((c, n) => c + this.points[n].z, 0);
        return (minA < minB) ? 1 : -1;
        };
        cx.translate(x, y);
        let points = this.points.map(n => {return {x:scale*n.x,y:scale*n.y, z:scale*n.z}});
        for (let face of this.faces.sort(compareZ)) {
        let light = 255 * ((this.faces.indexOf(face)) / this.faces.length);
        cx.fillStyle = `rgb(${light}, ${light}, ${light})`;
        cx.beginPath();
        cx.moveTo(points[face[0]].x, points[face[0]].y)
        for (let n = 1; n < face.length; n++)
            cx.lineTo(points[face[n]].x, points[face[n]].y);
        cx.closePath();
        cx.fill();
        }
        cx.resetTransform();
    }
}

function rotateAnimation(time, lastTime) {
    if (!lastTime) lastTime = time;
    if (decelerate) accel -= 0.008 * (time - lastTime);
    if (accel <= 0) {
        animRotate = null;
        return;
    }
    let angle = (Math.PI / 500) * accel;
    for (let n of t.points) {
        let newXZ = rotateCoord(0, 0, n.x, n.z, x * angle);
        let newYZ = rotateCoord(0, 0, newXZ.y, n.y, -y * angle);
        t.points[t.points.indexOf(n)] = v(newXZ.x, newYZ.y, newYZ.x);
    }
    drawT();
    animRotate = requestAnimationFrame(newTime => rotateAnimation(newTime, time, x, y));
}

function drawT() {
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (drawEdges) {
        t.drawSolidEdges(context, canvas.width / 2, canvas.height / 2, canvas.width / 5, t.edges.filter(edge => (t.points[edge[0]].z + t.points[edge[1]].z) >= 0));
        t.drawSolid(context, canvas.width / 2, canvas.height / 2, canvas.width / 5);
    }
    t.drawFaces(context, canvas.width / 2, canvas.height / 2, canvas.width / 10);
    if (drawEdges)
        t.drawSolidEdges(context, canvas.width / 2, canvas.height / 2, canvas.width / 5, t.edges.filter(edge => (t.points[edge[0]].z + t.points[edge[1]].z) < 0));
}

      let canvas = document.querySelector("canvas");
let context = canvas.getContext("2d");
let p = (1 + Math.sqrt(5)) / 2; // Golden ratio

let decelerate = false;
let tetrahedron = new Solid([v(1, 1, 1), v(1, -1, -1), v(-1, 1, -1), v(-1, -1, 1)], 3, [[2, 1, 3], [2, 0, 1], [2, 0, 3], [1, 0, 3]]);
let cube = new Solid([v(-1, -1, -1), v(1, 1, 1), v(-1, 1, -1), v(-1, 1, 1), v(1, 1, -1), v(1, -1, 1), v(1, -1, -1), v(-1, -1, 1)], 3, [[0, 6, 5, 7], [0, 2, 4, 6], [0, 7, 3, 2], [1, 3, 2, 4], [1, 4, 6, 5], [1, 5, 7, 3]]);
let octahedron = new Solid([v(1, 0, 0), v(-1, 0, 0), v(0, 1, 0), v(0, -1, 0), v(0, 0, 1), v(0, 0, -1)], 4, [[3, 4, 0], [3, 0, 5], [3, 5, 1], [3, 1, 4], [2, 0, 4], [2, 4, 1], [2, 1, 5], [2, 5, 0]])
let isosahedron = new Solid([v(0, 1, p), v(0, -1, -p), v(0, 1, -p), v(0, -1, p), v(1, p, 0), v(-1, -p, 0), v(1, -p, 0), v(-1, p, 0), v(p, 0, 1), v(-p, 0, -1), v(-p, 0, 1), v(p, 0, -1)], 5, [[7, 9, 2], [7, 10, 9], [7, 0, 10], [7, 4, 0], [7, 2, 4], [2, 9, 1], [2, 1, 11], [2, 11, 4], [9, 5, 10], [8, 4, 11], [8, 11, 6], [8, 6, 3], [8, 3, 0], [8, 0, 4], [6, 11, 1], [6, 1, 5], [6, 5, 3], [3, 0, 10], [3, 10, 5], [1, 9, 5]]);
let dodecahedron = new Solid([v(1, 1, 1), v(-1, 1, 1), v(-1, -1, 1), v(-1, -1, -1), v(1, -1, 1),v(1, -1, -1), v(-1, 1, -1), v(1, 1, -1), v(0, 1/p, p), v(0, -1/p, -p), v(0, -1/p, p), v(0, 1/p, -p), v(1/p, p, 0), v(-1/p, -p, 0), v(1/p, -p, 0), v(-1/p, p, 0), v(p, 0, 1/p), v(-p, 0, -1/p), v(-p, 0, 1/p), v(p, 0, -1/p)], 3, [[1, 18, 2, 10, 8], [19, 5, 9, 11, 7], [19, 16, 4, 14, 5], [16, 4, 10, 8, 0], [4, 10, 2, 13, 14], [14, 5, 9, 3, 13], [2, 13, 3, 17, 18], [3, 9, 11, 6, 17], [1, 18, 17, 6, 15], [12, 7, 11, 6, 15], [15, 1, 8, 0, 12], [12, 7, 19, 16, 0]]);
let custom = new Solid([v(1, 0, 0), v(-1, 0, 0), v(0, 1, 0),v(0, -1, 0),v(0.707, 0.707, 0), v(-0.707, 0.707, 0), v(-0.707, -0.707, 0), v(0.707, -0.707, 0),v(0, 0, -1), v(0, 0, 1), v(0, 0.707, 0.707), v(0, -0.707, 0.707), v(0, -0.707, -0.707), v(0, 0.707, -0.707),v(0.707, 0, 0.707), v(-0.707, 0, 0.707),v(-0.707, 0, -0.707),v(0.707, 0, -0.707)], 4, [[9, 14, 10],[9, 11, 14],[9, 11, 15],[9, 10, 15],[8, 12, 17],[8, 13, 17],[8, 13, 16],[8, 16, 12],[1, 5, 15],[1, 16, 6],[1, 5, 16],[1, 6, 15],[2, 4, 17, 13],[2, 10, 14, 4],[2, 5, 15, 10],[2, 13, 16, 5],[3, 12, 17, 7],[3, 7, 14, 11],[3, 11, 15, 6],[3, 6, 16, 12],[0, 14, 4],[0, 14, 7],[0, 7, 17],[0, 4, 17]]);
let t = isosahedron;
let solids = [custom, tetrahedron, cube, octahedron, isosahedron, dodecahedron];
let names = ["Custom Shape", "Tetrahedron", "Cube", "Octahedron", "Isosahedron", "Dodecahedron"]
let drawEdges = true;
context.strokeStyle = "red";
let angle = (Math.PI / 50);
for (let n of t.points) {
    let newXZ = rotateCoord(0, 0, n.x, n.z, angle);
    let newYZ = rotateCoord(0, 0, newXZ.y, n.y, -angle);
    t.points[t.points.indexOf(n)] = v(newXZ.x, newYZ.y, newYZ.x);
}
drawT();

let last = {x: null, y: null};
let accel = 2;
let x = 0.707, y = 0.5;
let animRotate = requestAnimationFrame((time) => rotateAnimation(time, 0));

let shapeName = document.querySelector("#blog1shapeName");
let controls = document.querySelector("#blog1controls").children;
controls[1].addEventListener("click", () => {
drawEdges = !drawEdges;
drawT();
console.log(t.points);
if (drawEdges) {
    decelerate = false;
    controls[1].style["border-color"] = "steelblue";
    controls[1].style.background = "steelblue";
}
else {
    decelerate = true;
    controls[1].style["border-color"] = "rgb(173, 173, 173)";
    controls[1].style.background = "rgb(173, 173, 173)";
}
});
controls[0].addEventListener("click", () => {
let index = (solids.indexOf(t) - 1);
t = solids[(index < 0) ? solids.length - 1 : index];
shapeName.innerHTML = names[index];
drawT();
});
controls[2].addEventListener("click", () => {
t = solids[(solids.indexOf(t) + 1) % solids.length];
shapeName.innerHTML = names[solids.indexOf(t)];
drawT();
});

canvas.addEventListener("mousedown", event => {
cancelAnimationFrame(animRotate);
animRotate = null;
last.x = event.clientX;
last.y = event.clientY;
});
canvas.addEventListener("mousemove", event => {
if (event.buttons != 1)
    return;
let distance = {x: event.clientX - last.x, y: event.clientY - last.y};
last.x = event.clientX; last.y = event.clientY;
accel = 5;
x = distance.x; y = distance.y;
if (animRotate == null)
    animRotate = requestAnimationFrame((time) => rotateAnimation(time, 0));
});


  }
}



</script>

<style scoped>
.triangle-left, .triangle-right{
	border-top: 10px solid transparent;
	border-bottom: 10px solid transparent;
	cursor: pointer;
}

.triangle-left:hover {
	border-right: 20px solid black;
}

.triangle-right:hover {
	border-left: 20px solid black;
}

.triangle-left {
	border-right: 20px solid gray;
}

.triangle-right {
	border-left: 20px solid gray;
}

.circle {
	width: 10px;
    height: 10px;
    border-radius: 50%;
    border-style: solid;
    border-color: steelblue;
    background: steelblue;
    cursor: pointer;
}

.visibleLink {
	color: royalblue;
}

.visibleLink:hover {
	color: purple;
}
</style>
